import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { values } from 'lodash';
import { BuildingId, DeviceId, EcsDevice, Group, GroupId } from 'shared/models';
import { DeviceLineItem } from '../deviceLineItem/DeviceLineItem';
import { Permission } from 'shared/permissions';
import { useDeviceTypes, useSession } from 'hooks';
import { useTranslation } from 'react-i18next';

require('./ElevatorTile.css');

export type Props = {
    buildingId: BuildingId | null;
    elevator: Group;
    externalId: string | null;
    devices: Record<DeviceId, EcsDevice>;
    online: Record<DeviceId, boolean>;
    editing: boolean;
    busy: boolean;
    onRemoveGroup(): void;
    onRemove(deviceId: DeviceId): void;
};

export const ElevatorTile = ({ buildingId, elevator, externalId, devices, online, editing, busy, onRemove, onRemoveGroup }: Props) => {
    const { deviceTypes } = useDeviceTypes();
    const { hasPermission } = useSession();
    const { t } = useTranslation();

    const elevatorId = elevator.id;

    const myDevices = useMemo(() => {
        return values(devices)
            .filter(d => d.groupId === elevatorId)
            .sort((a, b) =>
                a.deviceClass.localeCompare(b.deviceClass) ||
                (a.displayName || a.macAddress).localeCompare(b.displayName || b.macAddress)
            );
    }, [ devices, elevatorId ]);

    return (
        <tr
            id={ `elevator-tile${elevator.id}` }
            className="elevator-tile"
        >
            <td>
                <Link
                    className="_name"
                    to={ `/elevator/${elevator.id}` }
                >
                    { elevator.displayName || t('NO_NAME') }
                </Link>
                { editing && !myDevices.length &&
                    <button
                        type="button"
                        className="__remove-button ui tiny grey icon basic button"
                        disabled={ busy }
                        onClick={ onRemoveGroup }
                    >
                        { t('COMMAND.REMOVE') }
                    </button>
                }
                <div className="_ext-id">
                    {
                        externalId || <div dangerouslySetInnerHTML={ externalId ? undefined : { __html: '&nbsp' } }/>
                    }
                </div>
            </td>
            <td
                className={ myDevices.length ? undefined : '__no-devices' }
            >
                { myDevices.map(d =>
                    <DeviceLineItem
                        deviceTypes={ deviceTypes }
                        key={ d.deviceId }
                        device={ d }
                        online={ online[d.deviceId] }
                        busy={ busy }
                        showRemove={ editing && hasPermission(buildingId, Permission.EditDevice) }
                        showAssign={ false }
                        showDelete={ false }
                        onRemove={ () => onRemove(d.deviceId) }
                    />
                ) }
                { myDevices.length === 0 &&
                    t('ELEVATOR.NO_DEVICES')
                }
            </td>
        </tr>
    );
};
